import { deepClone } from '@policyfly/utils/type'

import { enumMappers } from './enum'
import { messageMappers } from './message'
import { createMessageFieldInfo, isArrayFieldInfo } from './utils'

import type { FormSource } from '../types/shared/formSource'
import type { TransformSchema } from '../types/transformers'
import type { AnyObject } from '@policyfly/types/common'
import type { FieldInfo, IMessageType } from '@protobuf-ts/runtime'

/**
 * Reverts a protobuf schema object back to a program configuration object.
 *
 * @example
 * import { SchemaSettings } from '@policyfly/protobuf'
 *
 * const settingsFile = await import('settings.ts')
 * const protobufSchema = convertSchema(SchemaSettings, settingsFile.default)
 * // `original` should be equivalent to `settingsFile.default`
 * const original = revertSchema(SchemaSettings, protobufSchema)
 */
export function revertSchema<
  T extends object,
  U extends TransformSchema<object, FormSource>
> (message: IMessageType<T>, protobuf: T): U {
  const definition = revertOrMap(message, protobuf)
  return (definition && deepClone(definition)) as U
}

/**
 * @internal
 *
 * Default handler for repeated messages.
 * Can be overridden by specifying `revertRepeated` for a message.
 */
export function revertOrMapRepeated<T extends object> (message: IMessageType<T>, protobufValue: T[]): object[] {
  return protobufValue.map((value) => {
    return revertOrMap(message, value) as T
  })
}

/**
 * @internal
 *
 * Utility to either use an existing reverter for an object or run it through the standard `revertMessage` function.
 */
export function revertOrMap<T extends object> (message: IMessageType<T>, protobuf: T): object | undefined {
  const mapper = messageMappers.getMapper(message)
  const output = (() => {
    if (mapper?.revertMessage) {
      return mapper.revertMessage(protobuf) as T | undefined
    }
    return revertMessage(
      createMessageFieldInfo(message),
      protobuf,
      {},
    )
  })()
  // remove undefined values
  return output && deepClone(output)
}

/**
 * @internal
 *
 * Using the `fieldInfo` for a message, reverts the `schemaObject` and writes it back to a `configObject`.
 *
 * Often it is better to use `revertOrMap` instead of this function.
 *
 * @param fieldInfo - The fieldInfo for the message. Must be a message kind.
 * @param schemaObject - The protobuf schema object to revert.
 * @param configObject - The resulting program config object to write to.
 */
export function revertMessage<T extends object> (fieldInfo: FieldInfo, schemaObject: T, configObject: AnyObject): AnyObject {
  // shouldn't be passing anything except messages here
  if (fieldInfo.kind !== 'message') {
    console.warn('writeMessage called with non-message fieldInfo', fieldInfo)
    return schemaObject
  }
  if (!schemaObject) {
    console.warn('writeMessage called with empty schemaObject', fieldInfo)
    return schemaObject
  }
  // loop through all the fields of the message
  for (const field of fieldInfo.T().fields) {
    const fieldName = field.jsonName as keyof T & string
    if (!(fieldName in schemaObject)) continue
    const protobufValue = schemaObject[fieldName]
    switch (field.kind) {
      case 'scalar':
        configObject[fieldName] = protobufValue
        break
      case 'enum': {
        const { revertEnum } = enumMappers.getMapper(field.T()[1]) ?? ({ revertEnum: (v) => v })
        if (isArrayFieldInfo(field)) {
          if (Array.isArray(protobufValue)) {
            configObject[fieldName] = protobufValue.map(revertEnum) as T[keyof T]
          }
        } else {
          configObject[fieldName] = revertEnum(protobufValue as number)
        }
        break
      }
      case 'message': {
        const message = field.T()
        if (isArrayFieldInfo(field)) {
          const { revertRepeated = revertOrMapRepeated.bind(null, message) } = messageMappers.getMapper(message) ?? {}
          configObject[fieldName] = revertRepeated(protobufValue as object[])
        } else {
          configObject[fieldName] = revertOrMap(message, protobufValue)
        }
        break
      }
    }
  }
  return configObject
}
